import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { SITE } from "@/config/site";

export interface LegalSection {
  id: string;
  title: string;
  body: string[];
}

interface LegalDocumentProps {
  /** Short label shown in the breadcrumb (e.g. "Privacy Policy") */
  crumb: string;
  /** Small uppercase eyebrow above the heading */
  eyebrow: string;
  /** Page-h1 heading */
  heading: string;
  /** One-line intro under the heading */
  subtitle: string;
  /** Revised date string shown near the heading */
  updated: string;
  /** SEO title */
  seoTitle: string;
  /** SEO description */
  seoDescription: string;
  /** SEO keywords */
  seoKeywords: string;
  /** Canonical path */
  path: string;
  /** Icon class (Remix Icon) for the eyebrow */
  icon: string;
  sections: LegalSection[];
}

/**
 * Shared renderer for legal / policy pages (privacy, terms, disclaimers,
 * refunds). Keeps headings, sidebar table-of-contents, typography and the
 * contact / grievance footer identical across every policy so the site stays
 * consistent and easy to maintain.
 */
export default function LegalDocument({
  crumb,
  eyebrow,
  heading,
  subtitle,
  updated,
  seoTitle,
  seoDescription,
  seoKeywords,
  path,
  icon,
  sections,
}: LegalDocumentProps) {
  return (
    <>
      <Seo title={seoTitle} description={seoDescription} keywords={seoKeywords} path={path} />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: crumb }]} />
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-800">
            <i className={icon}></i> {eyebrow}
          </span>
          <h1 className="mt-4 max-w-4xl font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
            {heading}
          </h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">{subtitle}</p>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Last updated: {updated}</p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
          <aside className="lg:sticky lg:top-24 h-fit">
            <nav className="rounded-lg border border-background-200 bg-background-50 p-4" aria-label="On this page">
              <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-foreground-500">On this page</h2>
              <ul className="mt-3 space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block rounded-md px-2 py-1.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700 cursor-pointer"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <article className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
            <div className="space-y-8">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">{s.title}</h2>
                  <div className="mt-3 space-y-3">
                    {s.body.map((p, i) => (
                      <p key={i} className="text-sm text-foreground-600 leading-relaxed">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              <section id="grievance" className="scroll-mt-24 rounded-lg border border-primary-200 bg-primary-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  Grievance Officer &amp; contact
                </h2>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  In accordance with the Information Technology Act, 2000 and the rules made thereunder, and the
                  Digital Personal Data Protection Act, 2023, the name and contact details of the Grievance Officer
                  are published below. Complaints are acknowledged within 48 hours and resolved within 30 days.
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-foreground-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-user-star-line mt-0.5 text-primary-700"></i>
                    <span>Grievance Officer, {SITE.name}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-mail-line mt-0.5 text-primary-700"></i>
                    <a href={`mailto:${SITE.email}`} className="break-all font-semibold text-primary-800">
                      {SITE.email}
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-phone-line mt-0.5 text-primary-700"></i>
                    <a href={`tel:${SITE.phoneHref}`} className="font-semibold text-primary-800">
                      {SITE.phone}
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-map-pin-2-line mt-0.5 text-primary-700"></i>
                    <span>{SITE.addressOneLine}</span>
                  </li>
                </ul>
                <p className="mt-3 text-xs text-foreground-500">
                  All disputes are subject to the exclusive jurisdiction of the courts at Mysore, Karnataka, India
                  (“Mysore Jurisdiction”).
                </p>
                <p className="mt-3 text-sm text-foreground-700">
                  You can also reach us through the{" "}
                  <Link to="/contact" className="font-semibold text-primary-800">
                    contact page
                  </Link>
                  .
                </p>
              </section>
            </div>
          </article>
        </div>
      </div>
    </>
  );
}